import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useAuth, usePermissions } from '../../hooks/useAuth';

const ProtectedRoute = ({ 
  children, 
  requiredPermission, 
  requiredPermissions, 
  requiredRole, 
  allowedRoles,
  requiredAction // New prop for role-based action checking
}) => {
  const { isAuthenticated, isLoading } = useSelector((state) => state.auth);
  const { hasRole, hasAnyRole, canPerformAction } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/home" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access denied. You need the '{requiredRole}' role to access this page.
        </Alert>
      </Box>
    );
  }

  if (allowedRoles && !hasAnyRole(allowedRoles)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access denied. Your role is not authorized to access this page.
          <br />Required roles: {allowedRoles.join(', ')}
        </Alert>
      </Box>
    );
  }

  // Check action-based permissions (new role-based approach)
  if (requiredAction && !canPerformAction(requiredAction)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access denied. You don't have permission to perform this action.
        </Alert>
      </Box>
    );
  }

  // Backward compatibility - check permission-based access
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access denied. You don't have the required permission to access this page.
        </Alert>
      </Box>
    );
  }

  if (requiredPermissions && !hasAnyPermission(requiredPermissions)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access denied. You don't have any of the required permissions to access this page.
        </Alert>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;
